import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { dateToLocalDateString, dateToPeriodLabel } from './weeklyReports';
import { priceTypingProcess, priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { localDateToUSFormat } from '../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection';
import { dateFormatter } from '../../supplementary_components/supplementaryMethods';
import cloneDeep from 'lodash/cloneDeep';
import { addPopup, addNotification } from '../../supplementary_components/store';
import { Form, Wrapper, Header, Content, Footer } from '../../general_components/forms/Form';
import { ConfirmationDialogue } from '../../general_components/forms/ConfirmationDialogue';
import { MdEdit } from 'react-icons/md';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import listStyles from '../../general_components/content_management/list_view/listView.module.scss';
import reportStyles from './reports.module.scss';
import { userHasAccess, userBelongsToCorrectDepartment } from '../../supplementary_components/userDataDelegate';
import { ThreeDotsButton } from '../../general_components/content_management/reusableItems/buttons';

const EXPENSE_GRID_COLUMNS = '15rem 0.6fr 0.6fr 0.6fr 2fr 3rem';

const WEEKDAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export function stripTime(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function parsePaymentDate(value) {
    if (!value) return null;
    if (value instanceof Date) return stripTime(value);
    const iso = String(value).match(/^\d{4}-\d{2}-\d{2}/);
    if (iso) return stripTime(new Date(`${iso[0]}T12:00:00`));
    const parsed = dateFormatter(String(value))?.date;
    if (parsed) return stripTime(new Date(`${parsed}T12:00:00`));
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : stripTime(d);
}

export function startOfWeekContaining(day, firstDayOfAWeek) {
    const d = stripTime(day);
    const targetDow = WEEKDAY_NAMES.indexOf((firstDayOfAWeek || 'monday').toLowerCase());
    if (targetDow < 0) return d;
    let diff = d.getDay() - targetDow;
    if (diff < 0) diff += 7;
    d.setDate(d.getDate() - diff);
    return d;
}

export function endOfWeekFromStart(weekStart) {
    const e = new Date(weekStart);
    e.setDate(e.getDate() + 6);
    return e;
}

export function groupExpensesByWeek(expenses, firstDayOfAWeek) {
    const buckets = new Map();
    for (const ex of expenses) {
        let weekStart;
        let weekEnd;
        if (ex.period?.startDate && ex.period?.endDate) {
            weekStart = parsePaymentDate(ex.period.startDate);
            weekEnd = parsePaymentDate(ex.period.endDate);
        } else {
            const pd = parsePaymentDate(ex.paymentDate);
            if (!pd) continue;
            weekStart = startOfWeekContaining(pd, firstDayOfAWeek);
            weekEnd = endOfWeekFromStart(weekStart);
        }
        if (!weekStart) continue;
        const key = dateToLocalDateString(weekStart);
        if (!buckets.has(key)) {
            buckets.set(key, {
                weekStart,
                weekEnd,
                expenses: [],
            });
        }
        buckets.get(key).expenses.push(ex);
    }
    const groups = [...buckets.values()].sort((a, b) => b.weekStart.getTime() - a.weekStart.getTime());
    for (const g of groups) {
        g.expenses.sort((a, b) => String(b.paymentDate || '').localeCompare(String(a.paymentDate || '')));
    }
    return groups;
}

export function normalizeDataset(res) {
    if (Array.isArray(res)) return res;
    if (res && Array.isArray(res.dataset)) return res.dataset;
    return [];
}

/** API: GET expense-categories — [{ id, name, builtin, custom }, ...]. Empty ⇒ no categories. */
function normalizeExpenseCategoryRows(res) {
    const list = normalizeDataset(res);
    if (!Array.isArray(list) || list.length === 0) return [];
    return list
        .map((item) => {
            if (item == null) return null;
            if (typeof item === 'string') {
                const s = item.trim();
                return s ? { id: s, name: s, builtin: s, custom: true } : null;
            }
            const name = item.name != null ? String(item.name).trim() : '';
            const builtin = item.builtin != null ? String(item.builtin).trim() : '';
            if (!name && !builtin) return null;
            return {
                id: item.id,
                name: name || builtin,
                builtin: builtin || name,
                custom: !!item.custom,
            };
        })
        .filter(Boolean)
        .sort((a, b) => {
            const isOther = (row) => String(row.builtin || '').toUpperCase() === 'OTHER';
            const aOther = isOther(a);
            const bOther = isOther(b);
            if (aOther && !bOther) return 1;
            if (!aOther && bOther) return -1;
            return (a.name || a.builtin).localeCompare(b.name || b.builtin, undefined, { sensitivity: 'base' });
        });
}

export function mergeById(prev, incoming) {
    const seen = new Set(prev.map((e) => e.id).filter((id) => id != null));
    const out = [...prev];
    let added = 0;
    for (const row of incoming) {
        if (row.id != null && seen.has(row.id)) continue;
        if (row.id != null) seen.add(row.id);
        out.push(row);
        added += 1;
    }
    return { merged: out, addedCount: added };
}

/** Oldest period start among rows (aligns with backend Period buckets). */
export function oldestPeriodStartAmong(items) {
    let min = null;
    for (const e of items || []) {
        const ps = e.period?.startDate ? parsePaymentDate(e.period.startDate) : null;
        if (!ps) continue;
        if (!min || ps.getTime() < min.getTime()) min = ps;
    }
    return min;
}

export function extractHasMoreOlder(res) {
    if (res && typeof res.hasMoreOlder === 'boolean') {
        return res.hasMoreOlder;
    }
    return true;
}

/** Same ordering as `groupExpensesByWeek` per-week sort: payment date desc, then id desc. */
function compareExpensesInList(a, b) {
    const c = String(b.paymentDate || '').localeCompare(String(a.paymentDate || ''));
    if (c !== 0) return c;
    return String(b.id ?? '').localeCompare(String(a.id ?? ''));
}

function extractExpenseFromApiSuccess(res) {
    if (res == null || typeof res !== 'object') return null;
    if (res.id != null && (res.paymentDate !== undefined || res.amount !== undefined)) return res;
    if (res.dataset != null) {
        const d = Array.isArray(res.dataset) ? res.dataset[0] : res.dataset;
        if (d && typeof d === 'object' && d.id != null) return d;
    }
    if (res.data != null && typeof res.data === 'object' && res.data.id != null) return res.data;
    if (res.expense != null && typeof res.expense === 'object') return res.expense;
    return null;
}

/** Insert new row or replace by id; keep list sorted for stable indices. */
function mergeExpenseIntoList(prev, expense) {
    if (!expense || expense.id == null) return prev;
    const idStr = String(expense.id);
    const idx = prev.findIndex((e) => String(e.id) === idStr);
    if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...expense };
        return next.sort(compareExpensesInList);
    }
    return [...prev, expense].sort(compareExpensesInList);
}

function buildExpenseAddForm(categoryRows) {
    const conf = cloneDeep(EXPENSE_ADD_FORM);
    const catCol = conf.columns.find((c) => c.name === 'category');
    if (catCol) catCol.dataset = Array.isArray(categoryRows) ? categoryRows : [];
    return conf;
}

/** Stable JSON value for <select> options; parsed in category column `processBeforeSending` to an object. */
function serializeExpenseCategoryForForm(option) {
    if (!option || typeof option !== 'object') return '';
    return JSON.stringify({
        id: option.id ?? null,
        name: String(option.name ?? option.builtin ?? ''),
        builtin: String(option.builtin ?? option.name ?? ''),
        custom: !!option.custom,
    });
}

/** Match stored expense.category (string builtin or object) to a row and return the same value as select options. */
function expenseCategoryToSelectValue(storedCategory, categoryRows) {
    const rows = Array.isArray(categoryRows) ? categoryRows : [];
    let row = null;
    if (storedCategory && typeof storedCategory === 'object') {
        row = rows.find(
            (r) =>
                (storedCategory.id != null && r.id === storedCategory.id) ||
                (storedCategory.builtin != null && String(storedCategory.builtin) === String(r.builtin)),
        );
    } else if (storedCategory != null && storedCategory !== '') {
        const s = String(storedCategory).trim();
        row = rows.find(
            (r) => r.builtin === s || r.name === s || (r.id != null && String(r.id) === s),
        );
    }
    if (!row) return '';
    return serializeExpenseCategoryForForm(row);
}

function ManageExpenseCategoriesPopup({ closePopup, postSuccessAction }) {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [newName, setNewName] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [adding, setAdding] = useState(false);

    const applyRows = useCallback((allRows) => {
        const customOnly = (allRows || []).filter((r) => r.custom && r.id != null);
        setRows(customOnly);
        const d = {};
        customOnly.forEach((r) => {
            d[r.id] = r.name;
        });
        setDrafts(d);
    }, []);

    const load = useCallback(() => {
        setLoading(true);
        sendRequest(
            'GET',
            API_ROOT + 'expense-categories',
            null,
            (res) => {
                applyRows(normalizeExpenseCategoryRows(res));
                setLoading(false);
            },
            () => {
                addNotification({ message: 'Could not load categories', type: 'alert' });
                setLoading(false);
            },
            null,
        );
    }, [applyRows]);

    useEffect(() => {
        load();
    }, [load]);

    const saveRename = (id) => {
        const name = String(drafts[id] ?? '').trim();
        const orig = rows.find((r) => r.id === id)?.name ?? '';
        if (!name) {
            addNotification({ message: 'Name cannot be empty', type: 'alert' });
            return;
        }
        if (name === orig) return;
        setBusyId(id);
        sendRequest(
            'PATCH',
            API_ROOT + `expense-categories/${encodeURIComponent(id)}`,
            null,
            () => {
                setBusyId(null);
                addNotification({ message: 'Category updated', type: 'success' });
                load();
                if (postSuccessAction) postSuccessAction();
            },
            () => {
                setBusyId(null);
                addNotification({ message: 'Could not update category', type: 'alert' });
            },
            { name },
        );
    };

    const addNew = () => {
        const name = newName.trim();
        if (!name) {
            addNotification({ message: 'Enter a category name', type: 'alert' });
            return;
        }
        setAdding(true);
        sendRequest(
            'POST',
            API_ROOT + 'expense-categories',
            null,
            () => {
                setAdding(false);
                setNewName('');
                addNotification({ message: 'Category added', type: 'success' });
                load();
                if (postSuccessAction) postSuccessAction();
            },
            () => {
                setAdding(false);
                addNotification({ message: 'Could not add category', type: 'alert' });
            },
            { name },
        );
    };

    return (
        <Wrapper>
            <Header><span>Manage expense categories</span></Header>
            <Content className={reportStyles.manageCategoriesContent}>
                {/* <p className={reportStyles.manageCategoriesHint}>
                    Only custom categories appear here. Built-in categories stay as defined by the system.
                </p> */}
                <p></p>
                {loading ? (
                    <LoadingIcon state={true} height="6rem" />
                ) : (
                    <>
                        {rows.length === 0 ? (
                            <p className={reportStyles.manageCategoriesEmpty}>No custom categories yet.</p>
                        ) : (
                            <ul className={reportStyles.manageCategoriesList}>
                                {rows.map((row) => (
                                    <li key={row.id} className={reportStyles.manageCategoriesRow}>
                                        <input
                                            type="text"
                                            className={reportStyles.manageCategoriesInput}
                                            value={drafts[row.id] ?? ''}
                                            onChange={(e) => setDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                                            disabled={busyId === row.id}
                                        />
                                        <button
                                            type="button"
                                            className={reportStyles.manageCategoriesSaveBtn}
                                            disabled={busyId === row.id || adding}
                                            onClick={() => saveRename(row.id)}
                                        >
                                            {busyId === row.id ? 'Saving…' : 'Save'}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className={reportStyles.manageCategoriesAddRow}>
                            <input
                                type="text"
                                className={reportStyles.manageCategoriesInput}
                                placeholder="New category name"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                disabled={adding}
                            />
                            <button
                                type="button"
                                className={reportStyles.manageCategoriesPrimaryBtn}
                                disabled={adding}
                                onClick={addNew}
                            >
                                {adding ? 'Adding…' : 'Add'}
                            </button>
                        </div>
                    </>
                )}
            </Content>
            <Footer>
                <div onClick={closePopup}>Close</div>
            </Footer>
        </Wrapper>
    );
}

function expenseRemoveFromListing(expense, postSuccessAction) {
    addPopup({
        content: ConfirmationDialogue,
        popupData: {
            confirmationDialogue: true,
            action: (postResultAction) => {
                sendRequest(
                    'DELETE',
                    API_ROOT + `expenses/${expense.id}`,
                    null,
                    () => {
                        postSuccessAction();
                        postResultAction(true);
                    },
                    () => {
                        addNotification({ message: 'Something Went Wrong', type: 'alert' });
                        postResultAction(true);
                    },
                );
            },
            cancelAction: (closePopup) => {
                closePopup();
            },
            name: 'Confirm',
            formTitle: 'Delete Expense',
            submitTitle: 'Delete',
            message: 'Are you sure you want to delete this expense?',
        },
        postSuccessAction: () => {},
        EZCloseAllowed: false,
    });
}

export const EXPENSE_ADD_FORM = {
    formAccessLevel: { key: 'EXPENSES', level: 'MANAGE' },
    formTitle: 'Add Expense',
    name: 'addExpenseForm',
    addLink: API_ROOT + 'expenses/add',
    successMessage: 'Expense was added',
    columns: [
        {
            label: 'Category',
            autoComplete: 'off',
            name: 'category',
            type: 'select',
            dataset: [],
            selectOptionValue: (option) => serializeExpenseCategoryForForm(option),
            processBeforeSending: (val) => {
                if (val == null || val === '') return null;
                try {
                    return JSON.parse(val);
                } catch {
                    return null;
                }
            },
        },
        {
            label: 'Payment Date',
            name: 'paymentDate',
            mandatory: true,
            autoComplete: 'off',
            calendarPickType: 'singleDate',
            onblur: (val) => dateFormatter(val)['date'],
            checks: [
                (value) => {
                    if (!dateFormatter(value)['date']) return 'Invalid Date';
                    return null;
                },
            ],
            processBeforeSending: (val) => dateToLocalDateString(new Date(val)),
        },
        {
            label: 'Amount',
            name: 'amount',
            mandatory: true,
            placeholder: '$0.00',
            autoComplete: 'off',
            filters: [priceTypingProcess],
            onblur: (val) => priceOnFinish(val),
            processBeforeSending: (val) => parseFloat(val.replace(/[^0-9.]/g, '')),
        },
        {
            label: 'Payment Method',
            autoComplete: 'off',
            name: 'method',
            type: 'select',
            dataset: ['', 'EFS', 'WIRE', 'CREDIT_CARD', 'CASH', 'ZELLE'],
            processBeforeSending: (val) => (val === '' ? null : val),
        },
        {
            label: 'Description',
            name: 'description',
            type: 'textArea',
            rowCount: 5,
        },
    ],
};

const EXPENSE_LISTING_CONF = {
    getRequestLink: API_ROOT + 'expenses',
    columnProportions: EXPENSE_GRID_COLUMNS,
    dataTitle: 'Expense',
    dataTitlePlural: 'Expenses',
    addButton: true,
    addConf: EXPENSE_ADD_FORM,
    editAccessLevel: { key: 'EXPENSES', level: 'MANAGE' },
    specialActions: [
        {
            requiredPermission: { key: 'EXPENSES', level: 'MANAGE' },
            buttonLabel: 'Manage Categories',
            icon: <MdEdit />,
            onClick: (requestReload) => {
                addPopup({
                    content: ManageExpenseCategoriesPopup,
                    popupData: {},
                    postSuccessAction: requestReload,
                    EZCloseAllowed: true,
                    customMinWidth: '28rem',
                });
            },
        },
    ],
    columns: [
        { label: 'Category', columnName: 'category' },
        {
            label: 'Payment Date',
            columnName: 'paymentDate',
            valueInterpreter: (data) => localDateToUSFormat(data),
        },
        {
            label: 'Amount',
            columnName: 'amount',
            valueInterpreter: (data) => priceOnFinish(data),
        },
        {
            label: 'Payment Method',
            columnName: 'method',
            valueInterpreter: (data) => data || '-',
        },
        {
            label: 'Description',
            columnName: 'description',
            valueInterpreter: (data) => data || '-',
        },
        {
            actionButtons: true,
            onRemoveAction: expenseRemoveFromListing,
        },
    ],
};

const DISPLAY_COLUMNS = EXPENSE_LISTING_CONF.columns.filter((c) => !c.actionButtons);

export const ExpensesPage = ({ companyData }) => {
    const firstDayOfAWeek = companyData?.firstDayOfAWeek || 'monday';
    const currentUserDepartment = localStorage.getItem('department');

    /** Rows from GET expense-categories (built-in + custom); drives select options and list labels. */
    const [customCategories, setCustomCategories] = useState([]);
    const [allExpenses, setAllExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadMoreBusy, setLoadMoreBusy] = useState(false);
    const [error, setError] = useState(null);
    const [hasMoreOlder, setHasMoreOlder] = useState(true);
    const [oldestLoadedPeriodStart, setOldestLoadedPeriodStart] = useState(null);
    const [reloadTick, setReloadTick] = useState(0);

    /** Refetch category labels/options only (does not reload the expense list). */
    const requestReload = useCallback(() => {
        setReloadTick((t) => t + 1);
    }, []);

    const applyExpenseFromApi = useCallback((apiValue) => {
        const row = extractExpenseFromApiSuccess(apiValue);
        if (row) {
            setAllExpenses((prev) => mergeExpenseIntoList(prev, row));
        } else {
            addNotification({
                message: 'Expense was saved but the response could not be merged into the list.',
                type: 'alert',
            });
        }
    }, []);

    useEffect(() => {
        const ac = new AbortController();
        sendRequest(
            'GET',
            API_ROOT + 'expense-categories',
            ac.signal,
            (res) => {
                setCustomCategories(normalizeExpenseCategoryRows(res));
            },
            () => setCustomCategories([]),
            null,
        );
        return () => ac.abort();
    }, [reloadTick]);

    const displayColumnsForTable = useMemo(
        () =>
            DISPLAY_COLUMNS.map((col) =>
                col.columnName === 'category'
                    ? {
                          ...col,
                          valueInterpreter: (val) => {return val['name']},
                      }
                    : col,
            ),
        [],
    );

    const addFormWithCategories = useMemo(() => buildExpenseAddForm(customCategories), [customCategories]);

    const actionColumn = useMemo(
        () => ({
            actionButtons: true,
            onEditAction: (expense) => {
                const addFormClone = cloneDeep(addFormWithCategories);
                addFormClone.formTitle = 'Edit Expense';
                addFormClone.customSubmitButton = 'Save Expense';
                const categoryColumn = addFormClone.columns.find((c) => c.name === 'category');
                if (categoryColumn) {
                    categoryColumn.defaultValue = () =>
                        expenseCategoryToSelectValue(expense.category, categoryColumn.dataset || []);
                }
                addFormClone.columns[1].defaultValue = () => localDateToUSFormat(expense.paymentDate);
                addFormClone.columns[2].defaultValue = () => priceOnFinish(expense.amount);
                addFormClone.columns[3].defaultValue = () => expense.method;
                addFormClone.columns[4].defaultValue = () => expense.description;
                addFormClone.columns.unshift({
                    hiddenField: true,
                    name: 'id',
                    defaultValue: () => expense.id,
                });
                addPopup({
                    content: Form,
                    popupData: { configuration: addFormClone },
                    postSuccessAction: (value) => {
                        applyExpenseFromApi(value);
                    },
                    EZCloseAllowed: false,
                });
            },
            onRemoveAction: expenseRemoveFromListing,
        }),
        [addFormWithCategories, applyExpenseFromApi],
    );

    const fetchExpensesResponse = useCallback((queryString, signal) => {
        const qs = queryString || '?pageNumber=0';
        return new Promise((resolve) => {
            sendRequest(
                'GET',
                API_ROOT + 'expenses' + qs,
                signal,
                (res) => resolve(res || {}),
                () => resolve({}),
                null,
            );
        });
    }, []);

    useEffect(() => {
        let cancelled = false;
        const ac = new AbortController();

        const run = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetchExpensesResponse('?pageNumber=0', ac.signal);
                if (cancelled) return;
                const rows = normalizeDataset(res);
                setAllExpenses(rows);
                setOldestLoadedPeriodStart(oldestPeriodStartAmong(rows));
                setHasMoreOlder(extractHasMoreOlder(res));
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
            ac.abort();
        };
    }, [fetchExpensesResponse]);

    const weekGroups = useMemo(
        () => groupExpensesByWeek(allExpenses, firstDayOfAWeek),
        [allExpenses, firstDayOfAWeek],
    );

    const userCanEdit = userHasAccess(EXPENSE_LISTING_CONF.editAccessLevel);

    const onLoadMore = useCallback(async () => {
        if (!hasMoreOlder || loadMoreBusy || !oldestLoadedPeriodStart || loading) return;
        setLoadMoreBusy(true);
        const ac = new AbortController();
        const qs = `?beforePeriodStart=${dateToLocalDateString(
            stripTime(oldestLoadedPeriodStart),
        )}&pageNumber=0`;
        try {
            const res = await fetchExpensesResponse(qs, ac.signal);
            const rows = normalizeDataset(res);
            setHasMoreOlder(extractHasMoreOlder(res));
            setAllExpenses((prev) => {
                const { merged } = mergeById(prev, rows);
                const nextOldest = oldestPeriodStartAmong(merged);
                if (nextOldest) {
                    setOldestLoadedPeriodStart(nextOldest);
                }
                return merged;
            });
        } finally {
            setLoadMoreBusy(false);
        }
    }, [fetchExpensesResponse, hasMoreOlder, loadMoreBusy, oldestLoadedPeriodStart, loading]);

    if (error) {
        return <div className="errorMessage">{error.customError ? error.customError : 'Something went wrong'}</div>;
    }

    if (loading) {
        return <LoadingIcon state={true} height="10rem" />;
    }

    return (
        <div className={reportStyles.mainContainer}>
            <div className={listStyles.pageHeader}>
                <div>{EXPENSE_LISTING_CONF.dataTitlePlural}</div>
                <div className={listStyles.actionsContainer}>
                    {EXPENSE_LISTING_CONF.specialActions
                        ? EXPENSE_LISTING_CONF.specialActions.map((action, index) => {
                              if (action.requiredPermission
                                    ? !userHasAccess(action.requiredPermission)
                                    : (action.requiredDepartment && currentUserDepartment !== action.requiredDepartment)) return null;
                              return (
                                  <div key={index} className={`${listStyles.addButton} non-selectable`}>
                                      <div onClick={() => action.onClick(requestReload)}>
                                          {action.buttonLabel}
                                          <div className="actionIcon">{action.icon}</div>
                                      </div>
                                  </div>
                              );
                          })
                        : null}
                    <div>
                        {EXPENSE_LISTING_CONF.addButton &&
                        userHasAccess(EXPENSE_LISTING_CONF.addConf.formAccessLevel) &&
                        (!EXPENSE_LISTING_CONF.addConf.formAccessDepartment ||
                            userBelongsToCorrectDepartment(EXPENSE_LISTING_CONF.addConf.formAccessDepartment)) ? (
                            <div
                                className={`${listStyles.addButton} non-selectable`}
                                onClick={() =>
                                    addPopup({
                                        content: Form,
                                        popupData: { configuration: addFormWithCategories },
                                        postSuccessAction: (value) => {
                                            applyExpenseFromApi(value);
                                        },
                                        EZCloseAllowed: addFormWithCategories.EZCloseAllowed ?? true,
                                    })
                                }
                            >
                                Add {EXPENSE_LISTING_CONF.dataTitle} +
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            <div
                className={listStyles.container}
                // eslint-disable-next-line
                style={{ ['--list-columns']: EXPENSE_GRID_COLUMNS }}
            >
                <div className={listStyles.listViewHeaderWrapper} style={{ gridTemplateColumns: 'var(--list-columns)' }}>
                    {displayColumnsForTable.map((col) => (
                        <div key={col.columnName} className={listStyles.listHeaderLabel}>
                            <span className={listStyles.labelText}>{col.label}</span>
                        </div>
                    ))}
                    <div className={listStyles.listHeaderLabel} />
                </div>

                {weekGroups.length === 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'var(--list-columns)', width: '100%' }}>
                        <div className={reportStyles.expensesEmptyBanner} style={{ gridColumn: '1 / -1' }}>
                            No {EXPENSE_LISTING_CONF.dataTitlePlural.toLowerCase()} in the loaded date range. Use “Load more” to fetch older periods.
                        </div>
                    </div>
                ) : (
                    weekGroups.map((group) => (
                        <React.Fragment key={dateToLocalDateString(group.weekStart)}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'var(--list-columns)', width: '100%' }}>
                                <div className={reportStyles.expensesWeekDividerInner} style={{ gridColumn: '1 / -1' }}>
                                    {dateToPeriodLabel(group.weekStart, false, 'weekly', group.weekEnd)}
                                </div>
                            </div>
                            {group.expenses.map((expense) => (
                                <div key={expense.id ?? `${expense.paymentDate}-${expense.amount}`} className={listStyles.row}>
                                    {displayColumnsForTable.map((col) => {
                                        const raw = expense[col.columnName];
                                        const text = col.valueInterpreter ? col.valueInterpreter(raw) : raw;
                                        return (
                                            <div key={col.columnName}>
                                                <span>{text}</span>
                                            </div>
                                        );
                                    })}
                                    <ThreeDotsButton
                                            disabled={!userCanEdit || expense.efsGenerated}
                                            position="relative"
                                            onEdit={() => actionColumn.onEditAction(expense)}
                                            onRemove={() =>
                                                actionColumn.onRemoveAction(expense, () => {
                                                    setAllExpenses((prev) =>
                                                        prev.filter((e) => String(e.id) !== String(expense.id)),
                                                    );
                                                })
                                            }
                                        />
                                </div>
                            ))}
                        </React.Fragment>
                    ))
                )}
            </div>

            {hasMoreOlder ? (
                <div className={listStyles.loadMoreButtonWrapper}>
                    <div
                        className={loadMoreBusy ? 'loading' : ''}
                        onClick={() => {
                            if (!loadMoreBusy) onLoadMore();
                        }}
                    >
                        {loadMoreBusy ? 'Loading…' : 'Load more'}
                    </div>
                </div>
            ) : (
                <div className={listStyles.loadMoreButtonWrapper}>
                    <div className={listStyles.noMoreToload}>No older expenses to load</div>
                </div>
            )}
        </div>
    );
};
